import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import jQuery from 'jquery';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getApiBaseUrl } from '../../utils/utils';
import { listShipments as listForwardedShipments, getShipment as getForwardedShipment, listCarriers } from './forwardingApi';
import ExportWizard from './export/ExportWizard';

/* ─── Status colour map ──────────────────────────────────────────────────── */
const STATUS_META = {
  ALL:                         { label: 'All',            color: '#6366f1', bg: '#eef2ff', icon: 'fa-th-large' },
  ASSIGNED:                    { label: 'Assigned',       color: '#f59e0b', bg: '#fef3c7', icon: 'fa-user' },
  DELIVERED:                   { label: 'Delivered',      color: '#10b981', bg: '#d1fae5', icon: 'fa-check-circle' },
  PLANNED:                     { label: 'Planned',        color: '#3b82f6', bg: '#dbeafe', icon: 'fa-calendar' },
  PICKED_UP:                   { label: 'Picked Up',      color: '#8b5cf6', bg: '#ede9fe', icon: 'fa-inbox' },
  PICKUP_ENROUTE:              { label: 'Pickup Enroute', color: '#06b6d4', bg: '#cffafe', icon: 'fa-truck' },
  PICKUP_REQUESTED:            { label: 'Pickup Req.',    color: '#0891b2', bg: '#cffafe', icon: 'fa-hand-paper-o' },
  DROP_ENROUTE:                { label: 'Drop Enroute',   color: '#0ea5e9', bg: '#e0f2fe', icon: 'fa-road' },
  FAILED:                      { label: 'Failed',         color: '#ef4444', bg: '#fee2e2', icon: 'fa-times-circle' },
  PENDING:                     { label: 'Pending',        color: '#f97316', bg: '#ffedd5', icon: 'fa-clock-o' },
  UNASSIGNED:                  { label: 'Unassigned',     color: '#64748b', bg: '#f1f5f9', icon: 'fa-question-circle' },
  INSCAN_AT_WAREHOUSE:         { label: 'In Warehouse',   color: '#7c3aed', bg: '#f3e8ff', icon: 'fa-archive' },
  DESTINATION_LINEHAUL_DEPART: { label: 'Linehaul',       color: '#0d9488', bg: '#ccfbf1', icon: 'fa-plane' },
  READY:                       { label: 'Ready',          color: '#2563eb', bg: '#dbeafe', icon: 'fa-flag' },
  RETURN_TO_SHIPPER:           { label: 'Return',         color: '#dc2626', bg: '#fee2e2', icon: 'fa-undo' },
  RTS:                         { label: 'RTS',            color: '#dc2626', bg: '#fee2e2', icon: 'fa-undo' },
  RETURNED:                    { label: 'Returned',       color: '#b91c1c', bg: '#fee2e2', icon: 'fa-reply' },
  CANCELLED:                   { label: 'Cancelled',      color: '#6b7280', bg: '#f3f4f6', icon: 'fa-ban' },
  FORWARDED:                   { label: 'Forwarded',      color: '#be185d', bg: '#fce7f3', icon: 'fa-share' },
  FORWARD_PENDING:             { label: 'Forward pending', color: '#d97706', bg: '#fef3c7', icon: 'fa-hourglass-half' },
  FORWARD_ISSUE:               { label: 'Forward issue',  color: '#dc2626', bg: '#fee2e2', icon: 'fa-exclamation-triangle' },
};

const statusMeta = (s) =>
  STATUS_META[(s || '').toUpperCase()] || { label: s || 'N/A', color: '#94a3b8', bg: '#f1f5f9', icon: 'fa-circle' };

const TABS = ['ALL','ASSIGNED','DELIVERED','PLANNED','PICKED_UP','PICKUP_ENROUTE','DROP_ENROUTE','FAILED','PENDING','FORWARDED'];

/* Forwarded orders come from the read-only forwarding API (the same source as the Forwarded Orders
   page), not from getOrdersByDateRangeAndStatus. When a carrier takes the delivery, the main backend
   never dispatches that leg to our fleet, so it has no DROP row here and Live View could not show it.
   The list is newest-first by id and pages at 100 with no date filter, so it is walked until it passes
   the start of the selected range (at most FORWARDED_MAX_PAGES), then narrowed to the range. */
const FORWARDED_PAGE_SIZE = 100;
const FORWARDED_MAX_PAGES = 20;

const localDay = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fmtDateTime = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* The day a forwarded leg falls on for the date range: when it was booked, else when it went out. */
const fwdDay = (f) => localDay(f.createdDate) || localDay(f.forwardedAt);

/* Join key between a forwarded leg and our rows. Booking ids reach the two systems in different
   shapes - "495-26A8099_1_test" on the forwarding side, "UB495-26A8099_test" on ours - so the
   non-prod "_test" suffix and the "UB" booking-reference prefix are folded away on both sides. */
const awbKey = (v) => (v == null ? '' : String(v).trim().toLowerCase().replace(/_test$/, '').replace(/^ub/, ''));

/* Forwarding states that mean the carrier does NOT have the parcel yet, or that someone must act.
   Shown as such: our own fleet was taken off these orders, so "Forwarded" would hide that nobody
   is delivering them. */
const FWD_PENDING   = ['PENDING', 'INFLIGHT', 'RETRY_SCHEDULED', 'AWAITING_APPROVAL', 'AWAITING_CONFIG', 'PENDING_MANUAL', 'SUSPENDED'];
const FWD_ISSUE     = ['FAILED_PERMANENT', 'EXCEPTION', 'INFLIGHT_UNKNOWN', 'CANCEL_MANUAL_REQUIRED', 'AMEND_MANUAL_REQUIRED'];
const FWD_CANCELLED = ['CANCELLED', 'CANCEL_REQUESTED', 'SUPERSEDED'];

/* The status a forwarded-only order shows under: cancelled / needs attention first, then the
   carrier's word mapped to ours once known, then whether it has actually gone to the carrier. */
const fwdStatus = (f) => {
  const st = String(f.status || '').toUpperCase();
  if (FWD_CANCELLED.includes(st)) return 'CANCELLED';
  if (FWD_ISSUE.includes(st)) return 'FORWARD_ISSUE';
  if (f.mappedTaskStatus) return String(f.mappedTaskStatus).toUpperCase();
  if (FWD_PENDING.includes(st)) return 'FORWARD_PENDING';
  if (st === 'DELIVERED' || st === 'RETURNED') return st;
  return 'FORWARDED';
};

/* Colour of the forwarding-status line in the Forwarded To column. */
const fwdTone = (f) => {
  const st = String(f.status || '').toUpperCase();
  return FWD_ISSUE.includes(st) ? '#dc2626' : FWD_PENDING.includes(st) ? '#d97706' : '#94a3b8';
};

/* A forwarded leg with no row of ours, in the Live View row shape. The list carries no consignee, so
   `c` is filled in later from the forwarding detail. The original record rides along as `forwarded`. */
const toLiveRow = (f, c, forwardedTo) => ({
  awb: f.legBookingId,
  businessRefNo: f.businessRefNo,
  type: fwdStatus(f),
  date: fwdDay(f) || null,
  consignee: c ? { name: c.name, phone: c.phone, email: '', address: [c.address, c.district].filter(Boolean).join(', ') } : undefined,
  city: c?.city,
  destination: c?.city,
  forwarded: f,
  forwardedTo,
  forwardedOnly: true,
});

/* Split a date range into chunks of `chunkDays` (default 7) — same approach as Billing Orders */
const splitDateRange = (startDate, endDate, chunkDays = 7) => {
  const chunks = [];
  const end = new Date(endDate);
  let currentStart = new Date(startDate);
  while (currentStart <= end) {
    const currentEnd = new Date(currentStart);
    currentEnd.setDate(currentEnd.getDate() + chunkDays - 1);
    if (currentEnd > end) currentEnd.setTime(end.getTime());
    chunks.push({ from: currentStart.toISOString().split('T')[0], to: currentEnd.toISOString().split('T')[0] });
    currentStart = new Date(currentEnd);
    currentStart.setDate(currentStart.getDate() + 1);
  }
  return chunks;
};

/* Unique key for an order row — order_id is unique per row; awb/job_id are shared across
   multi-piece/multi-leg rows, so never dedupe by them (that silently drops real rows). */
const rowKey = (o) => (
  o == null ? null
    : o.order_id != null ? 'O' + o.order_id
    : o.orderId != null ? 'O' + o.orderId
    : o.job_id != null ? 'J' + o.job_id
    : o.awb != null ? 'A' + o.awb
    : null
);


/**
 * Every field /getOrdersByDateRangeAndStatus returns, grouped for the Live View detail panel.
 *
 * The table itself shows nine columns because a wall board has to be readable at a glance, but the
 * response carries roughly twice that. Rather than widen the table until nothing is legible, the
 * rest live here and appear in place under the row. No extra request is made - this is the same
 * order object the table already holds.
 *
 * Each entry is [label, getter]; a getter returning null/'' is dropped, so a group with nothing in
 * it does not render at all and orders of different shapes (medical, COD, consolidated) each show
 * only what they actually have.
 */
const LIVE_DETAIL_GROUPS = [
  {
    // Only forwarded rows carry `forwarded`; on every other row these getters are null and the group is hidden.
    title: 'Forwarding',
    fields: [
      ['Forwarded to',      o => o.forwardedTo],
      ['Service country',   o => o.forwarded?.serviceCountry],
      ['Carrier AWB',       o => o.forwarded?.carrierHawb],
      ['Forwarding status', o => o.forwarded?.status],
      ['Hold reason',       o => o.forwarded?.holdReason],
      ['Carrier says',      o => (o.forwarded?.lastRemoteStatus ? `${o.forwarded.lastRemoteStatus}${o.forwarded.lastRemoteReason ? ` (${o.forwarded.lastRemoteReason})` : ''}` : null)],
      ['Carrier update at', o => fmtDateTime(o.forwarded?.lastRemoteEventAt)],
      ['Mapped to',         o => o.forwarded?.mappedTaskStatus],
      ['Forwarded at',      o => fmtDateTime(o.forwarded?.forwardedAt)],
      ['Carrier COD',       o => (o.forwarded?.codAmount == null ? null : `${o.forwarded.codCurrency || ''} ${Number(o.forwarded.codAmount).toFixed(2)}`.trim())],
      ['POD received by',   o => o.forwarded?.podReceiverName],
      ['Partner',           o => o.forwarded?.partnerName],
      ['Attempts',         o => (o.forwarded ? `${o.forwarded.attemptCount || 0} / ${o.forwarded.maxAttempts ?? '?'}` : null)],
      ['Last error',       o => (o.forwarded?.lastErrorCode ? `${o.forwarded.lastErrorCode}${o.forwarded.lastErrorMsg ? ` - ${o.forwarded.lastErrorMsg}` : ''}` : null)],
    ],
  },
  {
    title: 'Shipment',
    fields: [
      ['Order ID',        o => o.order_id ?? o.orderId],
      ['AWB',             o => o.awb],
      ['Client reference',o => o.businessRefNo],
      ['Master AWB',      o => o.masterAwbNumber],
      ['Leg / status',    o => o.type || o.status],
      ['Order type',      o => o.orderType],
      ['Service time',    o => o.serviceTime],
      ['Date',            o => o.date],
    ],
  },
  {
    // Declared by the partner with the order; older orders and partners that do not send them show nothing.
    title: 'Package',
    fields: [
      ['Weight (kg)',      o => o.consolidatedWeight],
      ['Parcels',          o => o.parcelDetails],
      ['Item description', o => o.itemDesc],
      ['Item summary',     o => o.itemSummary],
      ['Item value',       o => (o.orderValue === null || o.orderValue === undefined ? null : `${o.currency ? `${o.currency} ` : ''}${Number(o.orderValue).toFixed(2)}`)],
      ['Is jewellery',     o => (o.isJewellery === true ? 'Yes' : o.isJewellery === false ? 'No' : null)],
      ['Service type',     o => o.serviceType],
      ['Partner order no', o => o.partnerOrderNo],
    ],
  },
  {
    title: 'Route',
    fields: [
      ['Origin',      o => o.origin],
      ['Destination', o => o.destination],
      ['City',        o => o.city],
    ],
  },
  {
    title: 'Consignee',
    fields: [
      ['Name',                  o => o.consignee?.name],
      ['Phone',                 o => o.consignee?.phone],
      ['Email',                 o => o.consignee?.email || o.customerEmail],
      ['Address',               o => o.consignee?.address],
      ['Address details',       o => o.addressDetails],
      ['Area',                  o => o.areaName],
      ['National address',      o => o.nationalAddress],
      ['Delivery instructions', o => o.deliveryInstructions],
      ['Comments',              o => o.comments],
    ],
  },
  {
    title: 'Shipper / pickup',
    fields: [
      ['Name',        o => o.shipper?.name],
      ['Sender name', o => o.senderName],
      ['Phone',       o => o.shipper?.phone],
      ['Email',       o => o.shipper?.email],
      ['Address',     o => o.shipper?.address],
    ],
  },
  {
    title: 'Money',
    fields: [
      ['Payment type', o => o.paymentType],
      // 0 is a real, meaningful COD value, so it must not be filtered out as "empty".
      ['Total COD',    o => (o.totalCod === null || o.totalCod === undefined ? null : `AED ${Number(o.totalCod).toFixed(2)}`)],
      ['COD received', o => (o.codReceived === null || o.codReceived === undefined ? null : `AED ${Number(o.codReceived).toFixed(2)}`)],
    ],
  },
  {
    title: 'Assignment',
    fields: [
      ['Driver',    (o, driverName) => (o.driverId ? `${driverName(o.driverId) || 'Unknown'} (${o.driverId})` : null)],
      ['Project',   o => (o.projectName ? `${o.projectName}${o.projectId ? ` (#${o.projectId})` : ''}` : null)],
    ],
  },
  {
    title: 'Customer requests',
    fields: [
      ['Action code',        o => o.actionCode],
      ['Action description', o => o.actionDescription],
      ['Date change',        o => (o.isDateChange ? 'Requested' : null)],
      ['Address change',     o => (o.isAddressChange ? 'Requested' : null)],
      ['Delivery blocked',   o => (o.isBlockDelivery ? 'Yes' : null)],
    ],
  },
];


export default function AllOrders() {
  const API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab]         = useState('ALL');
  const [orders, setOrders]               = useState([]);
  const [allOrders, setAllOrders]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [fromDate, setFromDate]           = useState('');
  const [toDate, setToDate]               = useState('');
  const [selectedRows, setSelectedRows]   = useState([]);
  // AWB of the row expanded to full detail, or null. The table shows nine columns because more
  // than that is unreadable at a glance, but the API already returns roughly twice as many fields
  // per order - this panel surfaces the rest in place, without losing the row you were looking at
  // or making a second request. The eye button still opens the full page (timeline, POD, duties).
  const [expandedAwb, setExpandedAwb] = useState(null);
  const [drivers, setDrivers]             = useState([]);
  const [driverInfoMap, setDriverInfoMap] = useState({});
  const [partners, setPartners]           = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('0');
  const [searchQ, setSearchQ]             = useState('');
  const [sortKey, setSortKey]             = useState(null);   // column key currently sorted
  const [sortDir, setSortDir]             = useState('asc');  // 'asc' | 'desc'
  const [downloadFormat] = useState('excel'); // ticked-row download format
  // The export wizard: every order for the chosen filters, fetched a day at a time from the server.
  const [exportOpen, setExportOpen]       = useState(false);
  const [fetchError, setFetchError]       = useState('');
  // Forwarded legs whose day falls in the selected range; merged into the table further down.
  const [forwardedLegs, setForwardedLegs] = useState([]);
  const [forwardedState, setForwardedState] = useState({ loading: false, error: '', truncated: false });
  const [fwdConsignees, setFwdConsignees] = useState({});   // forwarded id -> consignee, per visible page
  const [carrierNames, setCarrierNames] = useState({});     // carrier code -> display name
  const fwdFetchIdRef = useRef(0);
  const fwdRangeRef = useRef('');

  /* Reassign modal */
  const [reassignAWB, setReassignAWB]     = useState(null);
  const [assigndate, setAssigndate]       = useState('');
  const [reassignMsg, setReassignMsg]     = useState('');

  const intervalRef  = useRef(null);
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  /* Chunked-fetch bookkeeping (for date ranges > 7 days — split + consolidate) */
  const abortControllerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const [fetchProgress, setFetchProgress] = useState({
    isActive: false, isComplete: false, currentChunk: 0, totalChunks: 0,
    successCount: 0, failedCount: 0, currentFromDate: '', currentToDate: '',
    failedRanges: [], totalRecords: 0, avgTimePerChunk: 0,
  });

  /* ── Init ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const toStr  = today.toISOString().split('T')[0];
    const fromDt = new Date(today);
    fromDt.setDate(today.getDate() - 5);
    const fromStr = fromDt.toISOString().split('T')[0];
    setFromDate(fromStr);
    setToDate(toStr);
    fetchDrivers();
    fetchPartners();
    fetchCarriers();
    getOrdersChange(fromStr, toStr, 'ALL');   // also loads the forwarded orders for the range
  }, []);

  /* ── Auto-refresh every 5 min ─────────────────────────────────────── */
  useEffect(() => {
    if (!fromDate || !toDate) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      getOrdersChange(fromDate, toDate, activeTabRef.current, undefined, true);  // silent = no flicker
    }, 300000);
    return () => clearInterval(intervalRef.current);
  }, [fromDate, toDate]);

  /* Driver-info lazy-load bookkeeping (effect defined after pagination below) */
  const fetchedIds  = useRef(new Set());
  const fetchingIds = useRef(new Set());

  /* ── Pagination (prevents rendering thousands of rows at once) ──────── */
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);

  /* ── API helpers ────────────────────────────────────────────────────── */
  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/driver/getAllDrivers`);
      if (res.data.success) setDrivers(res.data.data.map(d => ({ driver_id: d.driverId, driver_name: d.name })));
    } catch {}
  };

  const fetchPartners = async () => {
    try {
      const base = getApiBaseUrl();
      const res = await axios.get(`${base}/v1.0/partner/retrieve_list?page=0`);
      setPartners(res.data.data || []);
    } catch {}
  };

  const fetchCarriers = async () => {
    try {
      const list = await listCarriers();
      const names = {};
      (Array.isArray(list) ? list : []).forEach(c => {
        if (c && c.code) names[String(c.code).toUpperCase()] = c.displayName || c.code;
      });
      setCarrierNames(names);
    } catch { /* no carrier list: the code is shown instead */ }
  };

  const applyPartnerFilter = (data, pid) =>
    (pid && pid !== '0') ? data.filter(o => o.awb?.includes(`UB${String(pid).padStart(2, '0')}-`)) : data;

  // Defensive: tolerate different response shapes so data never silently vanishes
  const extractOrders = (res) =>
    res?.data?.data?.orders ?? res?.data?.orders ?? (Array.isArray(res?.data?.data) ? res.data.data : []);

  /**
   * Forwarded legs for [d1, d2]. Runs beside the order fetch and never blocks or fails it: a server
   * with no forwarding link (503) just shows none; any other failure is a note above the table.
   */
  const loadForwarded = async (d1, d2, silent = false) => {
    if (!d1 || !d2) return;
    fwdRangeRef.current = `${d1}|${d2}`;
    fwdFetchIdRef.current += 1;
    const myId = fwdFetchIdRef.current;
    if (!silent) setForwardedState(prev => ({ ...prev, loading: true }));
    try {
      const items = [];
      let truncated = false;
      for (let p = 0; ; p++) {
        if (p >= FORWARDED_MAX_PAGES) { truncated = true; break; }
        const data = await listForwardedShipments({ page: p, size: FORWARDED_PAGE_SIZE });
        if (myId !== fwdFetchIdRef.current) return;   // a newer range/refresh took over
        const pageItems = Array.isArray(data?.items) ? data.items : [];
        items.push(...pageItems);
        const total = Number(data?.total || 0);
        if (pageItems.length < FORWARDED_PAGE_SIZE || (total && items.length >= total)) break;
        // Newest-first by id: once a page ends before the range starts, every later page is older still.
        const lastDay = localDay(pageItems[pageItems.length - 1]?.createdDate);
        if (lastDay && lastDay < d1) break;
      }
      setForwardedLegs(items.filter(f => { const day = fwdDay(f); return day && day >= d1 && day <= d2; }));
      setForwardedState({ loading: false, error: '', truncated });
    } catch (e) {
      if (myId !== fwdFetchIdRef.current) return;
      setForwardedLegs([]);
      setForwardedState({
        loading: false, truncated: false,
        error: e?.status === 503 ? ''
          : `Forwarded orders could not be loaded${e?.status ? ` (HTTP ${e.status})` : ''}: ${e?.message || 'request failed'}`,
      });
    }
  };

  // `silent` = a background auto-refresh: keep the current rows on screen and swap the
  // new data in place (no full-screen spinner, no clearing) so the table never flickers.
  // `keepForwarded` = a plain tab switch: reuse the forwarded legs already loaded for this range.
  const getOrdersChange = async (d1, d2, status, partner, silent = false, keepForwarded = false) => {
    if (!d1 || !d2) return;
    if (!keepForwarded || fwdRangeRef.current !== `${d1}|${d2}`) loadForwarded(d1, d2, silent);
    if (status === 'FORWARDED') {
      // The Forwarded tab IS the forwarded legs; there is no own-fleet query to run for it.
      if (abortControllerRef.current) abortControllerRef.current.abort();
      fetchIdRef.current += 1;
      setOrders([]);
      setAllOrders([]);
      setFetchError('');
      setFetchProgress(prev => ({ ...prev, isActive: false, isComplete: false, failedCount: 0, failedRanges: [] }));
      if (!silent) setPage(1);
      setLoading(false);
      return;
    }

    // Cancel any in-flight fetch and claim this one as the latest
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    fetchIdRef.current += 1;
    const myId = fetchIdRef.current;

    if (!silent) setLoading(true);   // background refresh keeps the table visible (no flicker)
    setFetchError('');
    if (!silent) setPage(1);

    // Split into weekly chunks when the range is longer than 7 days (same as Billing Orders)
    const startDate = new Date(d1);
    const endDate = new Date(d2);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const chunks = daysDiff <= 7 ? [{ from: d1, to: d2 }] : splitDateRange(d1, d2, 7);

    const pid = partner !== undefined ? partner : selectedPartner;
    const ordersMap = new Map();           // consolidate across chunks, dedupe by AWB
    const completedRanges = [], failedRanges = [];
    let successCount = 0, failedCount = 0, totalTime = 0;

    setFetchProgress({
      isActive: chunks.length > 1, isComplete: false, currentChunk: 0, totalChunks: chunks.length,
      successCount: 0, failedCount: 0, currentFromDate: '', currentToDate: '',
      failedRanges: [], totalRecords: 0, avgTimePerChunk: 0,
    });
    if (!silent && chunks.length > 1) { setOrders([]); setAllOrders([]); }

    try {
      for (let i = 0; i < chunks.length; i++) {
        if (myId !== fetchIdRef.current) return;   // superseded by a newer fetch
        const chunk = chunks[i];
        const t0 = Date.now();
        setFetchProgress(prev => ({ ...prev, currentChunk: i + 1, currentFromDate: chunk.from, currentToDate: chunk.to }));
        try {
          const res = await axios.get(`${API_URL}shypvdriverapp/jobs/getOrdersByDateRangeAndStatus`, {
            params: { fromDate: chunk.from, toDate: chunk.to, status },
            signal: abortController.signal,
          });
          if (myId !== fetchIdRef.current) return;
          const data = extractOrders(res);
          // Dedup by the UNIQUE order_id (not awb) — multi-piece / multi-leg rows share an AWB, so
          // keying by awb silently dropped legitimate distinct rows from the list & download.
          data.forEach(o => { const key = rowKey(o); if (key != null && !ordersMap.has(key)) ordersMap.set(key, o); });
          successCount++;
          totalTime += Date.now() - t0;
          completedRanges.push({ from: chunk.from, to: chunk.to, count: data.length });
          // Update progressively so rows appear as each chunk lands
          const all = Array.from(ordersMap.values());
          const visible = applyPartnerFilter(all, pid);   // count that will actually show in the table
          setAllOrders(all);
          setOrders(visible);
          setFetchProgress(prev => ({ ...prev, successCount, totalRecords: visible.length, avgTimePerChunk: totalTime / successCount }));
        } catch (err) {
          if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
          if (myId !== fetchIdRef.current) return;
          failedCount++;
          failedRanges.push({ from: chunk.from, to: chunk.to, error: err.message });
          setFetchProgress(prev => ({ ...prev, failedCount, failedRanges: [...failedRanges] }));
        }
        if (i < chunks.length - 1) await new Promise(r => setTimeout(r, 150));   // ease off the server between chunks
      }

      if (myId !== fetchIdRef.current) return;

      // Retry any failed chunks (transient timeouts) — otherwise a single failed week silently
      // disappears from the list AND the download (the original "missing data" bug). Up to 2 passes.
      let retryPass = 0;
      while (failedRanges.length && retryPass < 2) {
        retryPass++;
        const toRetry = failedRanges.splice(0, failedRanges.length);
        setFetchProgress(prev => ({ ...prev, failedRanges: [] }));
        for (const chunk of toRetry) {
          if (myId !== fetchIdRef.current) return;
          try {
            const res = await axios.get(`${API_URL}shypvdriverapp/jobs/getOrdersByDateRangeAndStatus`, {
              params: { fromDate: chunk.from, toDate: chunk.to, status },
              signal: abortController.signal,
            });
            if (myId !== fetchIdRef.current) return;
            const data = extractOrders(res);
            data.forEach(o => { const key = rowKey(o); if (key != null && !ordersMap.has(key)) ordersMap.set(key, o); });
            successCount++;
            if (failedCount > 0) failedCount--;
            const all = Array.from(ordersMap.values());
            const visible = applyPartnerFilter(all, pid);
            setAllOrders(all);
            setOrders(visible);
            setFetchProgress(prev => ({ ...prev, successCount, failedCount, totalRecords: visible.length }));
          } catch (err) {
            if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
            failedRanges.push(chunk);
          }
          await new Promise(r => setTimeout(r, 250));
        }
      }
      setFetchProgress(prev => ({ ...prev, failedCount: failedRanges.length, failedRanges: [...failedRanges] }));

      if (failedRanges.length > 0) {
        const ranges = failedRanges.map(r => `${r.from}→${r.to}`).join(', ');
        setFetchError(`Incomplete: ${failedRanges.length} date range(s) failed after retries (${ranges}). The list & download are missing these rows — narrow the range or retry.`);
      } else if (ordersMap.size === 0 && failedCount > 0) {
        setFetchError(`Failed to load orders — ${failedCount} date range(s) failed. Please retry.`);
      }
    } finally {
      // Guarantee the latest fetch always clears loading/progress on every exit path
      if (myId === fetchIdRef.current) {
        setLoading(false);
        setFetchProgress(prev => ({ ...prev, isActive: false, isComplete: true }));
      }
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setOrders([]);
    setLoading(true);
    getOrdersChange(fromDate, toDate, tab, undefined, false, true);
  };

  const handlePartnerChange = (e) => {
    const pid = e.target.value;
    setSelectedPartner(pid);
    setPage(1);
    if (pid === '0') { setOrders(allOrders); return; }
    setOrders(allOrders.filter(o => o.awb?.includes(`UB${String(pid).padStart(2, '0')}-`)));
  };

  /* ── Order detail ───────────────────────────────────────────────────── */
  // Open the full-page order detail (Timeline / POD / Duties / Custody). The whole row is passed in
  // navigation state so the detail page can show the order summary instantly; awb + orderId also ride
  // in the query string so a refresh / shared link still loads the tab data.
  const openForwarded = (f) => { if (f && f.id != null) navigate(`/orders/forwarded-shipments/${f.id}`); };

  const goToDetail = (row) => {
    // A forwarded-only order has no order of ours to open; its page is the forwarding detail.
    if (row.forwardedOnly) { openForwarded(row.forwarded); return; }
    const oid = row.order_id ?? row.orderId ?? '';
    navigate(
      `/orders/order-details-view?awb=${encodeURIComponent(row.awb || '')}&orderId=${encodeURIComponent(oid)}`,
      { state: { row } }
    );
  };

  /* ── Reassign ───────────────────────────────────────────────────────── */
  const reAssign = async () => {
    if (!assigndate?.trim()) { setReassignMsg('Please select a date'); return; }
    try {
      const res = await axios.post(
        `${API_URL}shypvdriverapp/jobs/retryFailedOrders`,
        [{ awb: reassignAWB, orderDate: assigndate }],
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data.success) { setReassignMsg('Submitted for re-assignment'); setTimeout(() => setReassignAWB(null), 2000); }
    } catch { setReassignMsg('Error occurred'); }
  };

  /* ── Helpers ────────────────────────────────────────────────────────── */
  const driverName = id => {
    const d = drivers.find(dr => dr.driver_id === parseInt(id));
    return d ? `${id} - ${d.driver_name}` : id || '';
  };

  /* Sortable columns — one definition drives both the header cells and the sort itself. */
  const COLUMNS = [
    { key: 'awb',      label: 'AWB',        get: o => o.awb },
    { key: 'ref',      label: 'Client Ref', get: o => o.businessRefNo },
    { key: 'customer', label: 'Customer',   get: o => o.consignee?.name },
    { key: 'phone',    label: 'Phone',      get: o => o.consignee?.phone },
    { key: 'address',  label: 'Address',    get: o => o.consignee?.address },
    { key: 'driver',   label: 'Driver',     get: o => driverName(o.driverId) },
    { key: 'forwardedTo', label: 'Forwarded To', get: o => o.forwardedTo || '' },
    { key: 'status',   label: 'Status',     get: o => o.type || o.status },
    { key: 'date',     label: 'Date',       get: o => o.date },
    { key: 'project',  label: 'Project',    get: o => o.projectName },
  ];

  /**
   * Tokenised search: the query is split on whitespace and EVERY token must match somewhere
   * on the row (AND across tokens, OR across fields), so "dubai delivered 7000" narrows
   * progressively instead of matching a single substring in a single column.
   */
  /* ── Forwarded orders merged into the table ────────────────────────────
     A forwarded leg that ALSO has a row of ours (the carrier took only some legs, so our fleet kept the
     order) enriches that row. One with no row of ours (the carrier took the whole delivery, so it was
     never dispatched here) becomes a row of its own. Matched on AWB first, then on client ref. */
  const carrierLabel = (f) => carrierNames[String(f.carrierCode || '').toUpperCase()] || f.carrierCode || 'Carrier';
  const fwdByAwb = new Map();
  const fwdByOrder = new Map();
  const fwdByRef = new Map();
  const dupOrders = new Set();
  const dupRefs = new Set();
  forwardedLegs.forEach(f => {
    const k = awbKey(f.legBookingId);
    if (k && !fwdByAwb.has(k)) fwdByAwb.set(k, f);
    // Order-level booking id, for our AWBs that carry no leg suffix - only when one leg of that order was forwarded.
    const ok = awbKey(f.orderBookingId);
    if (ok) { if (fwdByOrder.has(ok)) dupOrders.add(ok); else fwdByOrder.set(ok, f); }
    // Our rows are DROP legs, so only a forwarded DELIVERY may claim one by client ref - and only by a
    // ref no other forwarded leg shares, so a reused ref ("NA", "-") never pins a carrier on a stranger.
    const r = awbKey(f.businessRefNo);
    if (r.length < 4 || String(f.jobType || '').toLowerCase() === 'pickup') return;
    if (fwdByRef.has(r)) dupRefs.add(r); else fwdByRef.set(r, f);
  });
  dupRefs.forEach(r => fwdByRef.delete(r));
  dupOrders.forEach(r => fwdByOrder.delete(r));
  // ...and only when that order has a single row of ours, so a forwarded KSA drop never tags its UAE sibling.
  const ownKeyCount = new Map();
  allOrders.forEach(o => { const k = awbKey(o.awb); ownKeyCount.set(k, (ownKeyCount.get(k) || 0) + 1); });
  const fwdFor = (o) => {
    const k = awbKey(o.awb);
    return fwdByAwb.get(k)
      || (ownKeyCount.get(k) === 1 ? fwdByOrder.get(k) : undefined)
      || fwdByRef.get(awbKey(o.businessRefNo));
  };
  const matchedFwdIds = new Set();
  allOrders.forEach(o => { const f = fwdFor(o); if (f) matchedFwdIds.add(f.id); });
  const unmatchedLegs = forwardedLegs.filter(f => !matchedFwdIds.has(f.id));
  const fwdPartnerOk = (f) => !selectedPartner || selectedPartner === '0'
    || String(f.fkPartnerId) === String(selectedPartner)
    || String(f.legBookingId || '').includes(`UB${String(selectedPartner).padStart(2, '0')}-`);
  const fwdRow = (f) => toLiveRow(f, fwdConsignees[f.id], carrierLabel(f));
  const displayRows = activeTab === 'FORWARDED'
    ? forwardedLegs.filter(fwdPartnerOk).map(fwdRow)
    : [
        ...orders.map(o => { const f = fwdFor(o); return f ? { ...o, forwarded: f, forwardedTo: carrierLabel(f) } : o; }),
        ...unmatchedLegs.filter(fwdPartnerOk).filter(f => activeTab === 'ALL' || fwdStatus(f) === activeTab).map(fwdRow),
      ];

  const searchTokens = searchQ.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = displayRows.filter(o => {
    if (!searchTokens.length) return true;
    const hay = [
      o.awb, o.businessRefNo, o.masterAwbNumber,
      o.consignee?.name, o.consignee?.phone, o.consignee?.address,
      o.shipper?.name, o.shipper?.phone,
      o.city, o.origin, o.destination,
      o.type, o.status, o.orderType, o.paymentType,
      o.projectName, o.serviceTime, o.date,
      o.totalCod, o.codReceived, o.consolidatedWeight, o.itemSummary,
      o.partnerOrderNo, o.areaName, o.itemDesc,
      driverName(o.driverId),
      o.forwardedTo, o.forwarded?.carrierCode, o.forwarded?.carrierHawb, o.forwarded?.status,
      o.forwarded?.lastRemoteStatus, o.forwarded?.serviceCountry, o.forwarded?.partnerName,
    ].map(v => (v == null ? '' : String(v).toLowerCase())).join(' ');
    return searchTokens.every(t => hay.includes(t));
  });

  /* Sorting — numeric when both values look numeric, otherwise locale string compare. */
  const sortCol = COLUMNS.find(c => c.key === sortKey);
  const sorted = !sortCol ? filtered : [...filtered].sort((a, b) => {
    const av = sortCol.get(a), bv = sortCol.get(b);
    const as = av == null ? '' : String(av), bs = bv == null ? '' : String(bv);
    if (as === '' && bs !== '') return 1;   // blanks always last
    if (bs === '' && as !== '') return -1;
    const an = Number(as), bn = Number(bs);
    const cmp = (!isNaN(an) && !isNaN(bn) && as !== '' && bs !== '')
      ? an - bn
      : as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  /* Reset to first page whenever the search term changes */
  useEffect(() => { setPage(1); }, [searchQ]);

  /**
   * Drop selected rows that the current filter no longer shows. Without this the selection
   * survives a partner/date reload or a new search, so "Selected" could exceed "All" and the
   * Selected download would emit rows that are not on screen.
   */
  useEffect(() => {
    setSelectedRows(prev => {
      if (!prev.length) return prev;
      const visible = new Set(filtered.map(r => r.awb));
      const next = prev.filter(r => visible.has(r.awb));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, forwardedLegs, searchQ]);

  /* Pagination: only render the current page of rows (was rendering ALL rows → froze) */
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  /* ── Lazy-load driver info, scoped to the VISIBLE page, concurrency-limited ── */
  const pageDriverKey = JSON.stringify(pageRows.map(o => o.driverId).filter(Boolean));
  useEffect(() => {
    const ids = [...new Set(pageRows.map(o => o.driverId).filter(Boolean))];
    const toFetch = ids.filter(id => !fetchedIds.current.has(id) && !fetchingIds.current.has(id));
    if (!toFetch.length) return;
    let cancelled = false;
    (async () => {
      const map = {};
      // Process in small batches so we never fire hundreds of XHRs at once
      const BATCH = 5;
      for (let i = 0; i < toFetch.length; i += BATCH) {
        const slice = toFetch.slice(i, i + BATCH);
        await Promise.all(slice.map(async id => {
          fetchingIds.current.add(id);
          try {
            const res = await axios.get(`${API_URL}shypvdriverapp/driver/getDriver`, { params: { driverId: id } });
            const d = res.data?.data;
            map[id] = { name: d?.name || '', team: d?.team?.teamName || '', zone: `${d?.zone?.district||''} - ${d?.zone?.emirates||''}` };
          } catch { map[id] = { name: '', team: '', zone: '' }; }
          finally { fetchingIds.current.delete(id); fetchedIds.current.add(id); }
        }));
        if (cancelled) return;
      }
      if (!cancelled) setDriverInfoMap(prev => ({ ...prev, ...map }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDriverKey]);

  /* Forwarded-only rows: the forwarding list carries no consignee, so fetch it for the VISIBLE page
     only, five at a time, once per leg. A database read on the main backend - no carrier call. */
  const fwdFetchedIds  = useRef(new Set());
  const fwdFetchingIds = useRef(new Set());
  const pageFwdKey = JSON.stringify(pageRows.filter(r => r.forwardedOnly).map(r => r.forwarded.id));
  useEffect(() => {
    const ids = [...new Set(pageRows.filter(r => r.forwardedOnly).map(r => r.forwarded.id))]
      .filter(id => id != null && !fwdFetchedIds.current.has(id) && !fwdFetchingIds.current.has(id));
    if (!ids.length) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < ids.length && !cancelled; i += 5) {
        const got = {};
        await Promise.all(ids.slice(i, i + 5).map(async id => {
          fwdFetchingIds.current.add(id);
          try {
            const d = await getForwardedShipment(id);
            if (d && d.consignee) got[id] = d.consignee;
          } catch { /* the row keeps its blanks; the forwarding detail page still has everything */ }
          finally { fwdFetchingIds.current.delete(id); fwdFetchedIds.current.add(id); }
        }));
        if (Object.keys(got).length) setFwdConsignees(prev => ({ ...prev, ...got }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFwdKey]);

  const counts = {};
  allOrders.forEach(o => {
    const s = (o.type || o.status || '').toUpperCase();
    counts[s] = (counts[s] || 0) + 1;
  });
  // Forwarded orders with no row of ours count under the status they show, and in All.
  unmatchedLegs.forEach(f => { const s = fwdStatus(f); counts[s] = (counts[s] || 0) + 1; });
  counts['ALL'] = allOrders.length + unmatchedLegs.length;
  counts['FORWARDED'] = forwardedLegs.length;

  /**
   * One row per order with EVERYTHING the accounts + operations teams ask for: identifiers,
   * customer/shipper, route, COD/finance, driver, status/dates, project, service time,
   * multishipment and weight. Built from the loaded rows, so the file always matches the table.
   */
  /**
   * Every column the export can produce, declared once.
   *
   * This was a hardcoded object literal, so the export was whatever twenty-two fields someone
   * picked at the time and nothing else - including no pickup address, on a delivery system.
   * Declaring them lets the operator choose, and lets new backend fields be exposed by adding one
   * line here rather than editing the mapper.
   *
   * `group` only drives the picker's layout. `get` receives the row plus the driver lookups, so a
   * column can resolve an id to a name without the caller knowing how.
   */
  const EXPORT_FIELDS = [
    { key: 'awb',        group: 'Shipment', label: 'AWB',                get: o => o.awb },
    { key: 'ref',        group: 'Shipment', label: 'Client Ref',         get: o => o.businessRefNo },
    { key: 'status',     group: 'Shipment', label: 'Status',             get: o => o.type },
    { key: 'orderType',  group: 'Shipment', label: 'Type',               get: o => o.orderType },
    { key: 'serviceTime',group: 'Shipment', label: 'Service Time',       get: o => o.serviceTime || '' },
    { key: 'project',    group: 'Shipment', label: 'Project',            get: o => o.projectName || '' },
    { key: 'master',     group: 'Shipment', label: 'Multishipment AWB',  get: o => o.masterAwbNumber || '' },
    { key: 'weight',     group: 'Shipment', label: 'Overall Weight',     get: o => (o.consolidatedWeight != null ? o.consolidatedWeight : '') },
    { key: 'orderId',    group: 'Shipment', label: 'Order ID',           get: o => o.order_id ?? o.orderId ?? '' },

    { key: 'customer',   group: 'Consignee', label: 'Customer Name',     get: o => o.consignee?.name },
    { key: 'phone',      group: 'Consignee', label: 'Phone',             get: o => o.consignee?.phone },
    { key: 'email',      group: 'Consignee', label: 'Email',             get: o => o.consignee?.email || '' },
    { key: 'address',    group: 'Consignee', label: 'Address',           get: o => o.consignee?.address },
    { key: 'city',       group: 'Consignee', label: 'City',              get: o => o.city },
    { key: 'destination',group: 'Consignee', label: 'Destination',       get: o => o.destination || o.city || '' },

    // The flagship gap: the export named the shipper but never said where to collect from.
    { key: 'shipper',       group: 'Pickup', label: 'Shipper',          get: o => o.shipper?.name },
    { key: 'shipperPhone',  group: 'Pickup', label: 'Shipper Phone',    get: o => o.shipper?.phone },
    { key: 'pickupAddress', group: 'Pickup', label: 'Pickup Address',   get: o => o.shipper?.address || '' },
    { key: 'senderName',    group: 'Pickup', label: 'Sender Name',      get: o => o.senderName || '' },
    { key: 'origin',        group: 'Pickup', label: 'Origin',           get: o => o.origin || o.shipper?.city || '' },

    { key: 'paymentType',group: 'Money', label: 'Payment Type',         get: o => o.paymentType },
    { key: 'cod',        group: 'Money', label: 'COD',                  get: o => o.totalCod },
    { key: 'codReceived',group: 'Money', label: 'COD Received',         get: o => o.codReceived },

    { key: 'driver',     group: 'Assignment', label: 'Driver',          get: (o, h) => h.driverName(o.driverId) },
    { key: 'driverTeam', group: 'Assignment', label: 'Driver Team',     get: (o, h) => h.driverInfoMap[o.driverId]?.team || '' },
    { key: 'driverZone', group: 'Assignment', label: 'Driver Zone',     get: (o, h) => h.driverInfoMap[o.driverId]?.zone || '' },
    { key: 'assigned',   group: 'Assignment', label: 'Assigned Date',   get: o => o.date },

    { key: 'actionCode', group: 'Requests', label: 'Action Code',       get: o => o.actionCode || '' },
    { key: 'actionDesc', group: 'Requests', label: 'Action Description',get: o => o.actionDescription || '' },
    { key: 'dateChange', group: 'Requests', label: 'Date Change',       get: o => (o.isDateChange ? 'Yes' : '') },
    { key: 'addrChange', group: 'Requests', label: 'Address Change',    get: o => (o.isAddressChange ? 'Yes' : '') },
    { key: 'blocked',    group: 'Requests', label: 'Delivery Blocked',  get: o => (o.isBlockDelivery ? 'Yes' : '') },

    { key: 'forwardedTo',   group: 'Forwarding', label: 'Forwarded To',      get: o => o.forwardedTo || '' },
    { key: 'carrierAwb',    group: 'Forwarding', label: 'Carrier AWB',       get: o => o.forwarded?.carrierHawb || '' },
    { key: 'fwdStatus',     group: 'Forwarding', label: 'Forwarding Status', get: o => o.forwarded?.status || '' },
    { key: 'carrierStatus', group: 'Forwarding', label: 'Carrier Status',    get: o => o.forwarded?.lastRemoteStatus || '' },
    { key: 'fwdCountry',    group: 'Forwarding', label: 'Service Country',   get: o => o.forwarded?.serviceCountry || '' },
    { key: 'forwardedAt',   group: 'Forwarding', label: 'Forwarded At',      get: o => fmtDateTime(o.forwarded?.forwardedAt) || '' },

    // Last, so the columns operators already rely on keep their positions.
    { key: 'itemSummary', group: 'Items', label: 'Item Summary', get: o => o.itemSummary || '' },
    { key: 'orderValue',  group: 'Items', label: 'Item Value',   get: o => (o.orderValue != null ? o.orderValue : '') },
    { key: 'isJewellery', group: 'Items', label: 'Is Jewellery', get: o => (o.isJewellery === true ? 'Yes' : o.isJewellery === false ? 'No' : '') },
  ];

  // The columns the export produced before it was selectable, so an operator who changes nothing
  // gets the file they have always got, in the same order - followed by where a forwarded order went.
  const DEFAULT_EXPORT_KEYS = [
    'awb', 'ref', 'customer', 'phone', 'address', 'city', 'shipper', 'shipperPhone',
    'origin', 'destination', 'paymentType', 'cod', 'codReceived', 'driver', 'driverTeam',
    'driverZone', 'status', 'orderType', 'assigned', 'project', 'serviceTime', 'master', 'weight',
    'forwardedTo', 'carrierAwb', 'carrierStatus', 'itemSummary', 'orderValue', 'isJewellery',
  ];

  const [exportKeys] = useState(DEFAULT_EXPORT_KEYS);

  const buildExport = (rowsIn) => {
    // Emit columns in the CATALOGUE's order, not the click order, so two people exporting the same
    // fields get the same spreadsheet.
    const chosen = EXPORT_FIELDS.filter(f => exportKeys.includes(f.key));
    const helpers = { driverName, driverInfoMap };
    return rowsIn.map(o => {
      const out = {};
      chosen.forEach(f => { out[f.label] = f.get(o, helpers); });
      return out;
    });
  };

  /** Download the given rows as .xlsx or .csv, honouring the chosen format. */
  const downloadRows = (rowsIn, baseName) => {
    if (!rowsIn || !rowsIn.length) { alert('Nothing to download for the current selection/filter.'); return; }
    // An empty field selection would write a sheet of blank rows, which looks like data loss.
    if (!exportKeys.length) { alert('No export fields configured.'); return; }
    const ws = XLSX.utils.json_to_sheet(buildExport(rowsIn));
    if (downloadFormat === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${baseName}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'LiveView');
      XLSX.writeFile(wb, `${baseName}.xlsx`);
    }
  };

  const downloadSelected = () => downloadRows(selectedRows, `live-view-selected-${activeTab || 'all'}`);

  /* Wizard defaults = what the page is showing now: same dates, partner and status tab. */
  const wizardFilters = {
    fromDate, toDate,
    partnerId: selectedPartner,
    statuses: ['ALL', 'PENDING', 'FORWARDED'].includes(activeTab) ? [] : [activeTab],
    legType: 'DROP',
    dateBasis: 'ORDER_DATE',
  };

  /* ════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
      <style>{`
        @keyframes fadeUp   { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes spin     { to{transform:rotate(360deg)} }
        .lv-row:hover { background:#f0f6ff !important; cursor:pointer; }
        .lv-tab-btn { border:none; cursor:pointer; border-radius:12px; padding:8px 14px; font-family:'Poppins',sans-serif; font-weight:600; font-size:12px; transition:all 0.18s; display:inline-flex; align-items:center; gap:6px; white-space:nowrap; }
        .lv-col-h { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.04em; padding:10px 14px; white-space:nowrap; text-align:left; }
        .lv-cell { font-size:13px; color:#334155; padding:12px 14px; vertical-align:middle; }
        .lv-badge { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; }
        .lv-action-btn { border:none; background:none; cursor:pointer; padding:5px 8px; border-radius:8px; font-size:13px; font-weight:600; transition:background 0.15s; }
        .lv-action-btn:hover { background:#e0e7ff; }
        input[type=checkbox] { accent-color:#3b82f6; width:15px; height:15px; cursor:pointer; }
      `}</style>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', padding: '18px 22px 16px', margin: '16px 16px 0', borderRadius: 14,
        border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          {/* Title */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#111827' }}>Live View</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700, color: '#047857' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
                LIVE
              </span>
            </div>
            <p style={{ margin: '3px 0 0', color: '#6b7280', fontSize: 12 }}>Real-time order tracking &amp; status overview</p>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select value={selectedPartner} onChange={handlePartnerChange} style={{
              background: '#fff', border: '1px solid #d1d5db', color: '#374151',
              borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', outline: 'none', minWidth: 150,
            }}>
              <option value="0">All Partners</option>
              {[...partners].sort((a,b) => a.pname.localeCompare(b.pname)).map(p => (
                <option key={p.pid} value={p.pid}>{String(p.pid).padStart(2,'0')} - {p.pname}</option>
              ))}
            </select>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '5px 10px' }}>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ border: 'none', background: 'transparent', color: '#374151', fontSize: 12, fontWeight: 600, outline: 'none' }} />
              <span style={{ color: '#9ca3af', fontSize: 12 }}>→</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ border: 'none', background: 'transparent', color: '#374151', fontSize: 12, fontWeight: 600, outline: 'none' }} />
            </div>

            <button onClick={() => getOrdersChange(fromDate, toDate, activeTab)} style={{
              background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8,
              padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <i className="fa fa-refresh" style={{ fontSize: 11 }} /> Refresh
            </button>

            {/* Downloads: the wizard pulls EVERY order for the chosen filters from the server (a day at a
                time), with Operations / Accounts / custom column layouts. Ticked rows still download
                straight from the table. */}
            <button onClick={() => downloadSelected()} disabled={selectedRows.length === 0}
              title="Download only the rows you ticked"
              style={{
                background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600,
                cursor: selectedRows.length ? 'pointer' : 'not-allowed', color: selectedRows.length ? '#2563eb' : '#9ca3af',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <i className="fa fa-check-square-o" style={{ fontSize: 11 }} /> Selected ({selectedRows.length})
            </button>
            <button onClick={() => setExportOpen(true)}
              title="Export wizard - choose filters and columns, then download every matching order"
              style={{
                background: '#1e3a8a', border: '1px solid #1e3a8a', color: '#fff', borderRadius: 8,
                padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <i className="fa fa-file-excel-o" style={{ fontSize: 12 }} /> Export
            </button>
          </div>
        </div>
      </div>

      {/* ── Status Tabs ─────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 16px 0', display: 'flex', gap: 8, flexWrap: 'wrap', animation: 'fadeUp 0.4s 0.07s ease-out both' }}>
        {TABS.map(tab => {
          const m = statusMeta(tab);
          const isActive = activeTab === tab;
          return (
            <button key={tab} className="lv-tab-btn"
              onClick={() => handleTabClick(tab)}
              style={{
                background: isActive ? m.color : '#fff',
                color: isActive ? '#fff' : m.color,
                border: `2px solid ${m.color}`,
                boxShadow: isActive ? `0 4px 14px ${m.color}40` : '0 1px 4px rgba(0,0,0,0.06)',
              }}>
              <i className={`fa ${m.icon}`} style={{ fontSize: 11 }} />
              {m.label}
              <span style={{
                background: isActive ? 'rgba(255,255,255,0.25)' : m.bg,
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 800, minWidth: 20, textAlign: 'center',
              }}>
                {counts[tab] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {(forwardedState.loading || forwardedState.error || forwardedState.truncated) && (
        <div style={{ padding: '8px 16px 0', fontSize: 12, fontWeight: 600, color: forwardedState.error ? '#b45309' : '#64748b' }}>
          <i className={`fa ${forwardedState.error ? 'fa-exclamation-triangle' : forwardedState.loading ? 'fa-spinner fa-spin' : 'fa-info-circle'}`} style={{ marginRight: 6 }} />
          {forwardedState.error
            || (forwardedState.loading ? 'Loading forwarded orders…'
            : `Only the latest ${FORWARDED_PAGE_SIZE * FORWARDED_MAX_PAGES} forwarded orders were checked — narrow the date range to see older ones.`)}
        </div>
      )}

      {/* ── Search ──────────────────────────────────────────────────────── */}
      <div style={{ padding: '12px 16px 0', animation: 'fadeUp 0.4s 0.11s ease-out both' }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <i className="fa fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
          <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="Search AWB, ref, name, phone…"
            style={{ width: '100%', boxSizing: 'border-box', paddingLeft: 34, height: 38, border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 13, outline: 'none', color: '#334155', background: '#fff' }} />
        </div>
      </div>

      {/* ── Fetch progress (large date ranges load in the background) ──────── */}
      {(fetchProgress.isActive || (fetchProgress.isComplete && fetchProgress.failedCount > 0)) && (
        <div style={{ margin: '12px 16px 0', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 18px', boxShadow: '0 2px 12px -6px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {fetchProgress.isActive && (
              <div style={{ width: 18, height: 18, border: '2px solid #e5e7eb', borderTop: '2px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
              {fetchProgress.isActive ? 'Loading orders…' : 'Done'}
            </span>
            <span style={{ fontSize: 13, color: '#0f172a' }}>
              <strong>{fetchProgress.totalRecords.toLocaleString()}</strong> records captured
            </span>
            {fetchProgress.isActive && fetchProgress.avgTimePerChunk > 0 && (
              <span style={{ fontSize: 13, color: '#64748b' }}>
                approx <strong style={{ color: '#6366f1' }}>{Math.max(1, Math.ceil(((fetchProgress.totalChunks - fetchProgress.successCount) * fetchProgress.avgTimePerChunk) / 1000))}s</strong> remaining
              </span>
            )}
          </div>

          <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginTop: 12 }}>
            <div style={{ width: `${fetchProgress.totalChunks > 0 ? (fetchProgress.successCount / fetchProgress.totalChunks) * 100 : 0}%`, height: '100%', background: '#10b981', borderRadius: 4, transition: 'width 0.3s ease' }} />
          </div>

          {fetchProgress.isComplete && fetchProgress.failedCount > 0 && (
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Some data could not be loaded.</span>
              <button onClick={() => getOrdersChange(fromDate, toDate, activeTab)}
                style={{ border: 'none', background: '#ef4444', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <i className="fa fa-refresh" style={{ marginRight: 6 }} />Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {fetchError && (
        <div style={{ margin: '12px 16px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span><i className="fa fa-exclamation-triangle" style={{ marginRight: 8 }} />{fetchError}</span>
          <button onClick={() => getOrdersChange(fromDate, toDate, activeTab)} style={{ border: 'none', background: '#b91c1c', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <i className="fa fa-refresh" style={{ marginRight: 6 }} />Retry
          </button>
        </div>
      )}

      {/* ── Table card ──────────────────────────────────────────────────── */}
      <div style={{ margin: '12px 16px 20px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px -4px rgba(0,0,0,0.08)', overflow: 'hidden', animation: 'fadeUp 0.4s 0.15s ease-out both' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e8edf2' }}>
                <th className="lv-col-h" style={{ width: 36, paddingLeft: 16 }}>
                  <input type="checkbox"
                    title="Select all rows matching the current filter"
                    onChange={e => setSelectedRows(e.target.checked ? [...sorted] : [])}
                    checked={sorted.length > 0 && selectedRows.length === sorted.length} />
                </th>
                {COLUMNS.map(c => {
                  const active = sortKey === c.key;
                  return (
                    <th key={c.key} className="lv-col-h" onClick={() => toggleSort(c.key)}
                      title={`Sort by ${c.label}`}
                      style={{ cursor: 'pointer', userSelect: 'none', color: active ? '#2563eb' : undefined }}>
                      {c.label}
                      <span style={{ marginLeft: 5, opacity: active ? 1 : 0.25, fontSize: 10 }}>
                        {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </th>
                  );
                })}
                <th className="lv-col-h">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(loading || (activeTab === 'FORWARDED' && forwardedState.loading && !forwardedLegs.length)) ? (
                <tr><td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, display: 'block', marginBottom: 10 }} />
                  Loading orders…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                  <i className="fa fa-inbox" style={{ fontSize: 36, opacity: 0.3, display: 'block', marginBottom: 8 }} />
                  {activeTab === 'FORWARDED'
                    ? 'No forwarded orders in the selected date range'
                    : selectedPartner !== '0'
                    ? `No orders for ${partners.find(p => String(p.pid) === String(selectedPartner))?.pname || 'this partner'} in the selected date range`
                    : 'No orders found'}
                </td></tr>
              ) : pageRows.map((row, i) => {
                const m = statusMeta(row.type || row.status);
                const isSel = selectedRows.some(r => r.awb === row.awb);
                return (
                  <React.Fragment key={row.awb || i}>
                  <tr className="lv-row"
                    style={{ borderBottom: '1px solid #f1f5f9', background: isSel ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafbff', animation: `fadeUp 0.3s ${Math.min(i, 30)*0.015}s ease-out both` }}>
                    <td className="lv-cell" style={{ paddingLeft: 16 }}>
                      <input type="checkbox" checked={isSel}
                        onChange={e => setSelectedRows(e.target.checked ? [...selectedRows, row] : selectedRows.filter(r => r.awb !== row.awb))} />
                    </td>
                    <td className="lv-cell">
                      <button onClick={() => goToDetail(row)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#3b82f6', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', textDecoration: 'underline dotted' }}>
                        {row.awb}
                      </button>
                    </td>
                    <td className="lv-cell" style={{ color: '#64748b', fontSize: 12 }}>{row.businessRefNo || '—'}</td>
                    <td className="lv-cell" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.consignee?.name || '—'}</td>
                    <td className="lv-cell" style={{ color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>{row.consignee?.phone || '—'}</td>
                    <td className="lv-cell" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: row.addressChange ? '#6366f1' : '#475569', fontSize: 12 }}>
                      {row.consignee?.address || '—'}
                    </td>
                    <td className="lv-cell" style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{driverName(row.driverId) || '—'}</td>
                    <td className="lv-cell" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {row.forwarded ? (
                        <div>
                          <button onClick={() => openForwarded(row.forwarded)} title="Open the forwarding detail"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#be185d', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                            <i className="fa fa-share" style={{ fontSize: 10, marginRight: 4 }} />
                            {row.forwardedTo}{row.forwarded.serviceCountry ? ` · ${row.forwarded.serviceCountry}` : ''}
                          </button>
                          {row.forwarded.carrierHawb && (
                            <div style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace' }} title="Carrier AWB">{row.forwarded.carrierHawb}</div>
                          )}
                          <div style={{ fontSize: 11, color: fwdTone(row.forwarded), fontWeight: fwdTone(row.forwarded) === '#94a3b8' ? 400 : 700 }} title="Forwarding status · the carrier's latest status">
                            {row.forwarded.status || '—'}{row.forwarded.lastRemoteStatus ? ` · ${row.forwarded.lastRemoteStatus}` : ''}
                          </div>
                        </div>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td className="lv-cell">
                      <span className="lv-badge" style={{ background: m.bg, color: m.color }}>
                        <i className={`fa ${m.icon}`} style={{ fontSize: 10 }} /> {m.label}
                      </span>
                      {row.actionCode && row.type?.toUpperCase() !== 'DELIVERED' && (
                        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{row.actionDescription}</div>
                      )}
                    </td>
                    <td className="lv-cell" style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{row.date || '—'}</td>
                    <td className="lv-cell" style={{ fontSize: 12, whiteSpace: 'nowrap', color: row.projectName ? '#1e40af' : '#cbd5e1', fontWeight: row.projectName ? 600 : 400 }}>
                      {row.projectName || '—'}
                    </td>
                    <td className="lv-cell">
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button className="lv-action-btn"
                          title={expandedAwb === row.awb ? 'Hide full details' : 'Show full details here'}
                          style={{ color: expandedAwb === row.awb ? '#0f766e' : '#64748b' }}
                          onClick={() => setExpandedAwb(expandedAwb === row.awb ? null : row.awb)}>
                          <i className={expandedAwb === row.awb ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} />
                        </button>
                        <button className="lv-action-btn" title="View Details" style={{ color: '#3b82f6' }} onClick={() => goToDetail(row)}>
                          <i className="fa fa-eye" />
                        </button>
                        {(row.type?.toUpperCase() === 'DELIVERED' || row.type?.toUpperCase() === 'PICKED_UP') && (
                          <button className="lv-action-btn" title="View POD" style={{ color: '#10b981' }} onClick={() => { goToDetail(row); }}>
                            <i className="fa fa-image" />
                          </button>
                        )}
                        {row.type?.toUpperCase() === 'FAILED' && !row.forwardedOnly && (
                          <button className="lv-action-btn" title="Reassign" style={{ color: '#f59e0b' }} onClick={() => { setReassignAWB(row.awb); setReassignMsg(''); setAssigndate(''); }}>
                            <i className="fa fa-refresh" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedAwb === row.awb && (
                    <tr key={`${row.awb || i}-detail`}>
                      <td colSpan={COLUMNS.length + 2} style={{ background: '#f8fafc', borderBottom: '2px solid #e8edf2', padding: '14px 18px' }}>
                        {LIVE_DETAIL_GROUPS.map(group => {
                          const shown = group.fields
                            .map(([label, get]) => [label, get(row, driverName)])
                            // Keep a field only when it carries something. A panel of forty "—"
                            // rows hides the handful of values that actually matter.
                            .filter(([, value]) => value !== null && value !== undefined && value !== '' && value !== '—');
                          if (!shown.length) return null;
                          return (
                            <div key={group.title} style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 }}>
                                {group.title}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                                {shown.map(([label, value]) => (
                                  <div key={label}>
                                    <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
                                    <div style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600, wordBreak: 'break-word' }}>{String(value)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={() => goToDetail(row)}
                          style={{ border: '1px solid #3b82f6', background: '#fff', color: '#3b82f6', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          {row.forwardedOnly ? 'Open forwarding detail — carrier events, label, POD' : 'Open full page — timeline, POD, duties, custody'}
                        </button>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer + pagination */}
        <div style={{ padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e8edf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
            {selectedRows.length > 0 ? `${selectedRows.length} selected · ` : ''}
            {filtered.length > 0
              ? `Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length} order${filtered.length !== 1 ? 's' : ''}`
              : '0 orders'}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {searchQ && (
              <button onClick={() => setSearchQ('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: 12 }}>
                <i className="fa fa-times" /> Clear
              </button>
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => setPage(1)} disabled={safePage === 1}
                  style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 9px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', color: safePage === 1 ? '#cbd5e1' : '#475569', fontSize: 12 }}>
                  <i className="fa fa-angle-double-left" />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
                  style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 9px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', color: safePage === 1 ? '#cbd5e1' : '#475569', fontSize: 12 }}>
                  <i className="fa fa-angle-left" /> Prev
                </button>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#334155', padding: '0 8px' }}>
                  Page {safePage} / {totalPages}
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                  style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 9px', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', color: safePage === totalPages ? '#cbd5e1' : '#475569', fontSize: 12 }}>
                  Next <i className="fa fa-angle-right" />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages}
                  style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 9px', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', color: safePage === totalPages ? '#cbd5e1' : '#475569', fontSize: 12 }}>
                  <i className="fa fa-angle-double-right" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ExportWizard
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        context="liveview"
        title="Export orders"
        partners={partners}
        initialFilters={wizardFilters}
        defaultPreset="operations"
      />

      {/* ══════════════════════════════════════════════════════════════════
          REASSIGN MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {reassignAWB && (
        <>
          <div onClick={() => setReassignAWB(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1100 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            background: '#fff', borderRadius: 20, padding: '28px', zIndex: 1101,
            width: 320, boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
            animation: 'fadeUp 0.25s ease-out both',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>Re-assign Order</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>{reassignAWB}</div>
              </div>
              <button onClick={() => setReassignAWB(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 30, height: 30, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Assignment Date</label>
            <input type="date" value={assigndate} onChange={e => setAssigndate(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', marginBottom: 16 }} />
            <button onClick={reAssign} style={{
              width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
              color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>Submit</button>
            {reassignMsg && <p style={{ marginTop: 10, fontSize: 12, color: reassignMsg.includes('Error') ? '#ef4444' : '#10b981', textAlign: 'center', margin: '10px 0 0' }}>{reassignMsg}</p>}
          </div>
        </>
      )}
    </div>
  );
}
